import _ from 'lodash';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import urljoin from 'url-join';

axiosRetry(axios, { retries: 3 });


export default class ApiRequestHelper {

  static setAxiosDefaults(values) {
    _.extend(axios.defaults, values);
  }

  constructor(apiConfig = null) {
    this.config = apiConfig || {};
  }

  get baseUrl() {
    return _.get(this.config, 'url', null);
  }

  buildUrl(path = '', queryParams = null) {
    let urlPath = this.buildUrlPath(path, queryParams);
    return urljoin(this.baseUrl, urlPath);
  }

  buildUrlPath(path = '', queryParams = null) {
    if (_.isArray(path)) {
      path = _.map(path, _.toString);  // make sure all args passed to urljoin are strings
      path = urljoin(...path);
    }
    let url = _.toString(path) || '';

    let queryParamsStr = this.buildQueryJsonString(queryParams);
    if (queryParamsStr) {
      let delim = _.includes(url, '?')? '&': '?';
      url += delim + queryParamsStr;
    }

    return url;
  }

  buildQueryJsonString(queryParams = null) {
    let str = '';
    if (!_.isEmpty(queryParams)) {
      str = 'json=' + encodeURIComponent(JSON.stringify(queryParams));
    }
    return str;
  }

  buildQueryUrlString(queryParams = null) {
    let str = '';
    if (!_.isEmpty(queryParams)) {
      str = _.chain(queryParams)
        .map((v, k) => {
          let v2 = null;
          if (!_.isUndefined(k)) {
            v2 = String(k);
            if (!_.isUndefined(v)) {
              v2 += '=' + String(v);
            }
          }
          return v2;
        })
        .compact()
        .join('&')
        .value();
    }
    return str;
  }

  getAuthTokenFromState() {
    return _.get(this.config, 'authToken', null);
  }

  buildRequestConfig(opts) {
    let config = null;
    if (opts && opts.auth) {
      const authToken = this.getAuthTokenFromState();
      if (authToken) {
        config = { headers: { Authorization: `Bearer ${authToken}` } };
      }
    }
    return config;
  }

  getRawRequest(path = '', queryParams = null, opts = null, errOpts = null) {
    let url = this.buildUrl(path, queryParams);
    let config = this.buildRequestConfig(opts);

    return axios.get(url, config)
      .then(this.handleRawResults.bind(this))
      .catch(err => this.handleApiError(err, { url, ...errOpts }));
  }

  get(path = '', queryParams = null, opts = null, errOpts = null) {
    let url = this.buildUrl(path, queryParams);
    let config = this.buildRequestConfig(opts);

    return axios.get(url, config)
      .then(this.handleApiResults.bind(this))
      .catch(err => this.handleApiError(err, { url, ...errOpts }));
  }

  put(path = '', putParams = null, queryParams = null, opts = null, errOpts = null) {
    let url = this.buildUrl(path, queryParams);
    let config = this.buildRequestConfig(opts);

    return axios.put(url, putParams, config)
      .then(this.handleApiResults.bind(this))
      .catch(err => this.handleApiError(err, { url, ...errOpts }));
  }

  post(path = '', postParams = null, queryParams = null, opts = null, errOpts = null) {
    let url = this.buildUrl(path, queryParams);
    let config = this.buildRequestConfig(opts);

    return axios.post(url, postParams, config)
      .then(this.handleApiResults.bind(this))
      .catch(err => this.handleApiError(err, { url, ...errOpts }));
  }

  postFile(path = '', postParams = null, queryParams = null, opts = null, errOpts = null) {
    let url = this.buildUrl(path, queryParams);
    let config = this.buildRequestConfig(opts) || {};
    config.headers = config.headers || {};
    config.headers['content-type'] = 'multipart/form-data';

    return axios.post(url, postParams, config)
      .then(this.handleApiResults.bind(this))
      .catch(err => this.handleApiError(err, { url, ...errOpts }));
  }

  patch(path = '', patchParams = null, queryParams = null, opts = null, errOpts = null) {
    let url = this.buildUrl(path, queryParams);
    let config = this.buildRequestConfig(opts);

    return axios.patch(url, patchParams, config)
      .then(this.handleApiResults.bind(this))
      .catch(err => this.handleApiError(err, { url, ...errOpts }));
  }

  delete(path = '', queryParams = null, opts = null, errOpts = null) {
    let url = this.buildUrl(path, queryParams);
    let config = this.buildRequestConfig(opts);

    return axios.delete(url, config)
      .then(this.handleApiResults.bind(this))
      .catch(err => this.handleApiError(err, { url, ...errOpts }));
  }

  handleRawResults(res) {
    return Promise.resolve(res);
  }

  handleApiResults(res) {
    let data = (res && res.data) || null;
    if (data && (data.action === 'error' || data.error)) {
      let err = data.error || 'API Request Error';
      return Promise.reject(err);
    }

    return Promise.resolve(data);
  }

  handleApiError(err, errOpts = null) {
    return Promise.reject(err);
  }

  resolve(res) {
    return Promise.resolve(res);
  }

  reject(err) {
    return Promise.reject(err);
  }
}

